import type { CompetitionRule } from '@shared/types/entities';
import { Eye, EyeOff, Plus, Trash2 } from 'lucide-react';

export type DraftRule = {
  id?: number;
  content: string;
  type: CompetitionRule['type'];
  isVisible: boolean;
  order: number;
};

const RULE_CONFIG: Record<CompetitionRule['type'], { bg: string; label: string }> = {
  WARNING: { bg: 'bg-red-50 border-red-200', label: '⚠ Avertissement' },
  IMPORTANT: { bg: 'bg-orange-50 border-orange-200', label: '⚡ Important' },
  INFO: { bg: 'bg-blue-50 border-blue-200', label: 'ℹ Information' },
};

interface Props {
  rules: DraftRule[];
  onChange: (rules: DraftRule[]) => void;
}

export function RulesEditor({ rules, onChange }: Props) {
  function addRule() {
    onChange([...rules, { content: '', type: 'INFO', isVisible: true, order: rules.length + 1 }]);
  }

  function updateRule(idx: number, updated: DraftRule) {
    onChange(rules.map((r, i) => (i === idx ? updated : r)));
  }

  function removeRule(idx: number) {
    onChange(rules.filter((_, i) => i !== idx));
  }

  return (
    <div className='space-y-3'>
      {rules.map((rule, idx) => {
        const config = RULE_CONFIG[rule.type];
        return (
          <div key={idx} className={`flex items-center gap-3 rounded-xl border p-3 ${config.bg}`}>
            <select
              value={rule.type}
              onChange={e => updateRule(idx, { ...rule, type: e.target.value as DraftRule['type'] })}
              className='shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-semibold focus:border-blue-500 focus:outline-none'
            >
              <option value='WARNING'>⚠ Avertissement</option>
              <option value='IMPORTANT'>⚡ Important</option>
              <option value='INFO'>ℹ Information</option>
            </select>
            <input
              type='text'
              value={rule.content}
              onChange={e => updateRule(idx, { ...rule, content: e.target.value })}
              placeholder='Contenu de la règle…'
              className='flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none'
            />
            <button
              type='button'
              title={rule.isVisible ? 'Visible — cliquer pour masquer' : 'Masquée — cliquer pour afficher'}
              onClick={() => updateRule(idx, { ...rule, isVisible: !rule.isVisible })}
              className={`shrink-0 rounded-lg p-2 transition ${
                rule.isVisible ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-slate-500'
              }`}
            >
              {rule.isVisible ? <Eye className='h-4 w-4' /> : <EyeOff className='h-4 w-4' />}
            </button>
            <button
              type='button'
              onClick={() => removeRule(idx)}
              className='shrink-0 rounded-lg p-2 text-red-400 transition hover:text-red-600'
            >
              <Trash2 className='h-4 w-4' />
            </button>
          </div>
        );
      })}
      {rules.length === 0 && (
        <p className='text-sm italic text-slate-400'>
          Aucune règle — les joueurs verront uniquement les informations de base du concours.
        </p>
      )}
      <button
        type='button'
        onClick={addRule}
        className='mt-1 flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800'
      >
        <Plus className='h-4 w-4' />
        Ajouter une règle
      </button>
    </div>
  );
}
